import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { backendClient } from '@/lib/backendClient';
import type { SessionRecord } from '@/types/session';
import type { PractitionerClient } from '@/types/practitioner';

export interface SharedSessionLink {
  id: string;
  session_id: string;
  practitioner_id: string;
  client_id?: string;
  created_by_user_id?: string;
  access_token: string;
  // Note: access_expires_at removed - links never expire
  total_plays: number; // Renamed from access_count
  unique_listeners: number;
  share_count: number;
  is_active: boolean;
  is_public: boolean;
  last_accessed_at?: string;
  delivery_method?: 'email' | 'sms' | 'link';
  delivery_status?: 'created' | 'sent' | 'accessed' | 'completed';
  created_at: string;
  updated_at: string;
}

export interface CreateClientSessionRequest {
  client_id: string;
  category_id: string;
  title: string;
  session_type: 'sample' | 'custom' | 'personalized' | 'preset';
  session_notes?: string;
  expires_hours?: number;
  delivery_method?: 'email' | 'sms' | 'link';
}

export const usePractitionerSessions = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [clientSessions, setClientSessions] = useState<SessionRecord[]>([]);
  const [sharedLinks, setSharedLinks] = useState<SharedSessionLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClientSessions = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching sessions for practitioner:', user.id);
      
      const { data, error } = await backendClient
        .from('sessions')
        .select(`
          *,
          practitioner_clients!client_id (
            id,
            client_name,
            email
          )
        `)
        .or(`practitioner_id.eq.${user.id},and(practitioner_id.is.null,user_id.eq.${user.id})`)
        .order('created_at', { ascending: false });

      console.log('📊 Sessions query result:', { data, error });
      console.log('📈 Sessions count found:', data?.length || 0);

      if (error) throw error;
      setClientSessions((data || []).map(session => ({
        ...session,
        session_type: session.session_type as 'sample' | 'custom' | 'personalized' | 'preset',
        client_feedback: (session.client_feedback || {}) as Record<string, any>
      })));
    } catch (err) {
      console.error('Error fetching client sessions:', err);
      setError('Failed to fetch client sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedLinks = async () => {
    if (!user) return;
    
    try {
      console.log('🔗 Fetching shared links for practitioner:', user.id);
      
      // Practitioners can still view their own shared links due to RLS policy
      const { data, error } = await backendClient
        .from('shared_session_links')
        .select('*')
        .eq('practitioner_id', user.id)
        .order('created_at', { ascending: false });

      console.log('🔗 Shared links query result:', { data, error });
      
      if (error) {
        console.error('Shared links query error:', error);
        // Don't throw error - just log it and continue with empty array
        console.warn('Could not fetch shared links, continuing with empty array');
        setSharedLinks([]);
        return;
      }
      
      console.log('🔗 Shared links count found:', data?.length || 0);
      setSharedLinks((data || []).map(link => ({
        ...link,
        delivery_method: link.delivery_method as 'email' | 'sms' | 'link',
        delivery_status: link.delivery_status as 'created' | 'sent' | 'accessed' | 'completed'
      })));
    } catch (err) {
      console.error('Error fetching shared links:', err);
      // Set empty array instead of leaving it undefined
      setSharedLinks([]);
    }
  };

  const createClientSession = async (
    sessionData: Omit<SessionRecord, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
    clientId: string,
    sessionNotes?: string,
    expiresHours: number = 168 // 7 days default
  ): Promise<SessionRecord | null> => {
    if (!user) return null;
    
    try {
      // Create the session
      const { data: session, error: sessionError } = await backendClient
        .from('sessions')
        .insert({
          ...sessionData,
          user_id: user.id,
          practitioner_id: user.id,
          client_id: clientId,
          assigned_to: clientId,
          delivery_status: 'created',
          session_notes: sessionNotes,
        })
        .select()
        .single();

      if (sessionError) throw sessionError;
      
      await fetchClientSessions();
      
      toast({
        title: "Session Created",
        description: "Client session has been created successfully.",
      });
      
      return {
        ...session,
        session_type: session.session_type as 'sample' | 'custom' | 'personalized' | 'preset',
        client_feedback: (session.client_feedback || {}) as Record<string, any>
      };
    } catch (err) {
      console.error('Error creating client session:', err);
      toast({
        title: "Error",
        description: "Failed to create client session. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const createSharedLink = async (
    sessionId: string,
    clientId?: string,
    expiresHours: number = 168
  ): Promise<string | null> => {
    try {
      // Check if session already has an active shared link
      const existingLink = getSharedLinkForSession(sessionId);
      if (existingLink) {
        const shareUrl = getSessionShareUrl(existingLink.access_token);
        toast({
          title: "Link Already Exists",
          description: "This session already has an active share link.",
        });
        return shareUrl;
      }

      const { data, error } = await backendClient.rpc('create_shared_session_link', {
        p_session_id: sessionId,
        p_client_id: clientId,
        p_expires_hours: expiresHours
      });

      if (error) throw error;

      // The RPC returns an array with the full link object
      if (!data || data.length === 0) {
        throw new Error('Failed to create shared link');
      }

      const createdLink = data[0];

      await fetchSharedLinks();
      await fetchClientSessions();

      const shareUrl = `${window.location.origin}/listen/${createdLink.access_token}`;
      
      toast({
        title: "Share Link Created",
        description: "Session share link has been generated successfully.",
      });
      
      return shareUrl;
    } catch (err: any) {
      console.error('Error creating shared link:', err);
      const errorMessage = err.message || 'Unknown error occurred';
      toast({
        title: "Failed to Create Share Link",
        description: `Error: ${errorMessage}. Please try again.`,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateSharedLink = async (
    linkId: string, 
    updates: Partial<SharedSessionLink>
  ): Promise<boolean> => {
    try {
      const { error } = await backendClient
        .from('shared_session_links')
        .update(updates)
        .eq('id', linkId);

      if (error) throw error;
      
      await fetchSharedLinks();
      
      toast({
        title: "Link Updated",
        description: "Shared link has been updated successfully.",
      });
      
      return true;
    } catch (err) {
      console.error('Error updating shared link:', err);
      toast({
        title: "Error",
        description: "Failed to update shared link. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const deactivateSharedLink = async (linkId: string): Promise<boolean> => {
    return updateSharedLink(linkId, { is_active: false });
  };

  const getSessionShareUrl = (accessToken: string): string => {
    return `${window.location.origin}/listen/${accessToken}`;
  };

  const getSharedLinkForSession = (sessionId: string): SharedSessionLink | undefined => {
    return sharedLinks.find(link => link.session_id === sessionId && link.is_active);
  };

  useEffect(() => {
    if (user) {
      fetchClientSessions();
      fetchSharedLinks();
    }
  }, [user]);

  return {
    clientSessions,
    sharedLinks,
    loading,
    error,
    fetchClientSessions,
    fetchSharedLinks,
    createClientSession,
    createSharedLink,
    updateSharedLink,
    deactivateSharedLink,
    getSessionShareUrl,
    getSharedLinkForSession,
  };
};
